import { PackageIcon } from '@phosphor-icons/react';
import {
  packageSizeText,
  packageWeightText,
  type PackageBox,
} from '../../lib/packages';

export type PackagesCardProps = {
  boxes: PackageBox[];
  isLoading?: boolean;
  /** The rate call's own reason, shown in place of the generic empty-quote line. */
  unavailableReason?: string;
};

/**
 * The boxes this order will ship in. Read-only: the warehouse works them out
 * from the size of each product, and the rates on offer are the price of these
 * boxes — so a wrong product size shows up here first.
 */
export function PackagesCard({
  boxes,
  isLoading,
  unavailableReason,
}: PackagesCardProps) {
  return (
    <section className="sk:flex sk:flex-col sk:border-t sk:border-rule sk:pt-3">
      <div className="sk:flex sk:flex-col">
        {isLoading && boxes.length === 0 ? (
          <div className="sk:my-2 sk:h-8 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary" />
        ) : null}

        {boxes.map(box => (
          <div
            key={box.id}
            className="sk:flex sk:flex-wrap sk:items-center sk:gap-x-2 sk:gap-y-1 sk:border-b sk:border-rule sk:py-2 sk:first:pt-0 sk:last:border-b-0 sk:last:pb-0"
          >
            <PackageIcon
              size={14}
              weight="duotone"
              className="sk:shrink-0 sk:text-fg-tertiary"
            />
            <span className="sk:text-dense sk:text-fg-tertiary">
              {boxes.length > 1 ? `Package ${box.id}` : 'Package'}
            </span>
            <span className="sk:text-dense sk:font-semibold sk:text-fg">
              {packageSizeText(box)}
            </span>
            <span className="sk:text-dense sk:text-fg-secondary">
              {packageWeightText(box)}
            </span>
          </div>
        ))}

        {!isLoading && unavailableReason ? (
          <span className="sk:pt-2 sk:text-dense sk:text-fg-tertiary">
            {unavailableReason}
          </span>
        ) : null}

        {!isLoading && boxes.length === 0 && !unavailableReason ? (
          <span className="sk:pt-2 sk:text-dense sk:text-fg-tertiary">
            The fulfillment provider didn&apos;t provide package details.
          </span>
        ) : null}
      </div>
    </section>
  );
}
